import React from 'react'
import useSEO from 'src/hooks/useSEO';
import Header from 'src/layouts/Header';
import Footer from 'src/layouts/Footer';

const PrivacyPolicy = () => {
  useSEO({
    title: 'Privacy Policy - SphynxPlace.com',
    description: 'Read SphynxPlace.com privacy policy to understand how we collect, use, and protect your personal information.',
    robots: 'index, follow',
  });
  return (
    <>
    <Header />
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl md:text-4xl font-bold mb-8">Privacy Policy</h1>
      
      <div className="mb-6 text-gray-600">
        <p><strong>Effective date:</strong> 1.2.2026</p>
        <p><strong>Website:</strong> sphynxplace.com</p>
        <p><strong>Contact:</strong> <a href="mailto:support@sphynxplace.com" className="text-blue-600 hover:underline">support@sphynxplace.com</a></p>
      </div>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Scope</h2>
        <p className="mb-4">
          This Privacy Policy explains how SphynxPlace ("we", "us", "our") collects, uses, stores, and protects personal data when you access or use our website and services, including account creation, posting adverts, browsing listings, saving favourites, contacting other users, and reporting content.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Marketplace Notice</h2>
        <p className="mb-4">
          SphynxPlace operates solely as an online listing and communication platform. We do not act as a breeder, seller, buyer, transporter, escrow service, or veterinary provider, and we are not a party to any transaction between users. All listings, photos, descriptions, health information, pedigree claims, and other details are provided directly by users.
        </p>
        <p className="mb-4">
          SphynxPlace does not verify, guarantee, or assume responsibility for the accuracy of listings, the appearance, health, temperament, or condition of any cat, or the outcome of any transaction. Users are solely responsible for conducting their own due diligence before buying or adopting a cat.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Information We Collect</h2>
        <p className="mb-4">We may collect the following categories of personal data:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>Account and profile information, such as name, email address, country, postal code, account type (individual, breeder, or company), and verification status</li>
          <li>Contact details, including phone number (required for posting adverts) and messages submitted through contact or inquiry forms</li>
          <li>Advert and listing content voluntarily provided by users, including descriptions, images, prices, locations, health and documentation selections, and other listing details</li>
          <li>Engagement data such as saved favourites and user interactions with listings</li>
          <li>Reports and moderation-related information submitted when reporting adverts or users</li>
          <li>Technical and usage data, including IP address, device information, browser type, pages visited, and security logs</li>
          <li>Cookies and similar technologies used to maintain sessions, remember preferences, and improve platform performance</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">How We Use Your Information</h2>
        <p className="mb-4">Personal data is used to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Create and manage user accounts</li>
          <li>Enable posting, displaying, and managing adverts and profiles</li>
          <li>Facilitate communication between users</li>
          <li>Display contact details according to platform rules (e.g. phone numbers visible only to registered users)</li>
          <li>Provide favourites and reporting functionality</li>
          <li>Monitor and prevent fraud, abuse, and prohibited content</li>
          <li>Improve platform functionality, performance, and security</li>
          <li>Communicate service-related updates, verification notices, or policy changes</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Legal Basis for Processing</h2>
        <p className="mb-4">
          Where applicable under GDPR or similar regulations, we process personal data based on contractual necessity, legitimate interests (such as security and fraud prevention), user consent (where required), and legal obligations.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Sharing of Information</h2>
        <p className="mb-4">We may share personal data:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>With other users, where necessary to display adverts, profiles, or contact options</li>
          <li>With trusted service providers who assist in operating the platform (hosting, email delivery, analytics, security), under appropriate data protection agreements</li>
          <li>With social login providers if you choose to register or log in using third-party services</li>
          <li>When required by law or to protect the rights, safety, and integrity of the platform and its users</li>
        </ul>
        <p className="mb-4">SphynxPlace does not sell personal data to third parties.</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">International Data Transfers</h2>
        <p className="mb-4">
          As a global platform, your data may be processed or stored outside your country of residence. Where required, appropriate safeguards are applied to protect such transfers.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Data Retention</h2>
        <p className="mb-4">
          Personal data is retained only for as long as necessary to provide services, comply with legal obligations, resolve disputes, and enforce platform policies. Data may be deleted or anonymized once it is no longer required.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Account Deletion</h2>
        <p className="mb-4">
          Users may close their account at any time via the account settings. Following account deletion, associated personal data will be removed or anonymized where possible. Certain information may be retained for a limited period where required by law, for security purposes, fraud prevention, dispute resolution, or enforcement of these Terms and platform policies.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Security Measures</h2>
        <p className="mb-4">
          We apply reasonable technical and organizational measures to protect personal data, including secure storage, access controls, and encryption where appropriate. However, no system can guarantee absolute security, and users are responsible for safeguarding their account credentials.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">User Rights</h2>
        <p className="mb-4">
          Depending on your jurisdiction, you may have rights to access, correct, delete, restrict, or object to the processing of your personal data, as well as the right to data portability. Requests can be submitted via the contact details listed above.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Children's Privacy</h2>
        <p className="mb-4">
          SphynxPlace is not intended for children under the age of 16. We do not knowingly collect personal data from individuals under 16 years of age. If we become aware that such data has been collected, it will be deleted without delay.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Third-Party Links</h2>
        <p className="mb-4">
          Listings or pages may contain links to third-party websites or services. SphynxPlace is not responsible for the privacy practices or content of external sites.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Changes to This Policy</h2>
        <p className="mb-4">
          We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. Continued use of the platform constitutes acceptance of the revised policy.
        </p>
      </section>
    </div>
    <Footer />
    </>
  );
};

export default PrivacyPolicy;